<template>
    <div class="w-full">
        <PageHeader type="Song" :title="song.name" :image="imageCover" :image-subtitle="song.artist.name">
            <template #meta>
                <span class="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <img v-if="song.artist.image" :src="song.artist.image" alt="" class="h-6 w-6 rounded-full object-cover" />
                    <Link :href="'/artist/' + song.artist.id" class="font-semibold text-white hover:underline">{{ song.artist.name }}</Link>
                    <template v-for="artist in (song.featuring || [])" :key="artist.id">
                        <span>,</span>
                        <Link :href="'/artist/' + artist.id" class="font-semibold text-white hover:underline">{{ artist.name }}</Link>
                    </template>
                    <template v-if="song.formatted_duration"><span class="mx-1">·</span>{{ song.formatted_duration }}</template>
                </span>
                <p v-if="song.origin_artist" class="mt-1">
                    Originally sung by
                    <Link :href="'/artist/' + song.origin_artist.id" class="text-white hover:underline">{{ song.origin_artist.name }}</Link>
                </p>
            </template>
            <template #actions>
                <SmartPlay :song="song" v-if="song.meta && song.meta.music" />
                <button v-else aria-label="play song" title="Play"
                    @click="prepareCurrent({ id: song.id, type: 'SONG', name: song.name || song.title })"
                    class="nl-play-fab h-14 w-14 text-xl">
                    <span class="icon-play translate-x-0.5"></span>
                </button>
                <button @click="toggleFavorite(song)" :title="isLiked(song) ? 'Remove from liked songs' : 'Like'"
                    :aria-label="isLiked(song) ? 'remove from liked songs' : 'like'" :aria-pressed="isLiked(song)" class="nl-btn-icon !text-2xl"
                    :class="isLiked(song) ? '!text-nomad-accent' : ''">
                    <span class="icon-heart"></span>
                </button>
                <button v-if="song.id" @click="copyLink" title="Copy link" aria-label="share url" class="nl-btn-icon">
                    <span class="icon-link"></span>
                </button>
                <button v-if="song.youtube" @click="openYoutube" aria-label="youtube video" title="Watch video" class="nl-btn-icon">
                    <span class="icon-youtube"></span>
                </button>
                <button v-if="!lyricsText" @click="addLyrics" title="Add lyrics" aria-label="add lyrics" class="nl-btn-icon">
                    <span class="icon-file-text"></span>
                </button>
                <a v-if="song.youtube" :href="'https://www.youtube.com/watch?v=' + song.youtube"
                    aria-label="watch on youtube" title="Open on YouTube" target="_blank" class="nl-btn-icon">
                    <span class="icon-external-link"></span>
                </a>
            </template>
        </PageHeader>

        <AdSlot placement="songTop" class="mb-10" />

        <div class="grid grid-cols-1 gap-12 xl:grid-cols-12">
            <section class="xl:col-span-7">
                <h2 class="nl-section-title mb-4">Lyrics</h2>
                <p v-if="lyricsText" class="whitespace-pre-line text-lg leading-9 text-nomad-100" dir="auto">{{ lyricsText }}</p>
                <!-- Same entry point as the app: only offered when the song has no lyrics -->
                <button v-else type="button" @click="addLyrics"
                    class="flex w-full max-w-md items-center gap-3 rounded-xl bg-white/[0.06] p-4 text-left transition-colors hover:bg-white/[0.09]">
                    <span class="icon-music text-xl text-nomad-accent" aria-hidden="true"></span>
                    <span class="min-w-0 flex-1">
                        <span class="block text-sm font-semibold text-white">Lyrics unavailable</span>
                        <span class="mt-0.5 block text-xs text-nomad-400">Contribute lyrics</span>
                    </span>
                    <span class="icon-chevron-right text-nomad-400" aria-hidden="true"></span>
                </button>
            </section>

            <aside class="space-y-10 xl:col-span-5">
                <Link :href="'/artist/' + song.artist.id"
                    class="flex items-center gap-4 rounded-lg p-2 transition-colors hover:bg-white/[0.05]">
                    <img v-if="song.artist.image" :src="song.artist.image" :alt="song.artist.name" class="h-16 w-16 rounded-full object-cover" />
                    <span class="min-w-0">
                        <span class="block text-xs text-nomad-400">Artist</span>
                        <span class="block truncate font-semibold text-white">{{ song.artist.name }}</span>
                    </span>
                </Link>

                <section v-if="song.similarSongs && song.similarSongs.length">
                    <h2 class="nl-section-title mb-3 px-0.5">Similar songs</h2>
                    <TrackList :songs="song.similarSongs" />
                </section>

                <section v-if="song.playlists && song.playlists.length">
                    <h2 class="nl-section-title mb-3 px-0.5">Featured in</h2>
                    <div class="flex flex-wrap gap-2">
                        <Link v-for="playlist in song.playlists" :key="playlist.id" :href="'/playlist/' + playlist.id" class="nl-chip">
                            {{ playlist.name }}
                        </Link>
                    </div>
                </section>

                <!-- Desktop only: stays in view while reading long lyrics -->
                <div class="hidden xl:sticky xl:top-20 xl:block">
                    <AdSlot placement="songSidebar" size="rectangle" />
                </div>
            </aside>
        </div>

        <AdSlot placement="songBottom" class="mt-14" />

        <div class="mt-14">
            <SongComments
                :song-id="song.id"
                :initial-comments="initialComments"
                :initial-has-more="commentsHaveMore"
            />
        </div>

        <section class="mt-6 flex flex-col items-start justify-between gap-4 border-t border-white/[0.06] pt-8 sm:flex-row sm:items-center">
            <div>
                <h3 class="font-semibold text-white">Listen on the Heesify app</h3>
                <p class="mt-1 text-sm text-nomad-400">Songs, lyrics, podcasts and audiobooks.</p>
            </div>
            <div class="flex flex-wrap gap-2">
                <a href="https://apps.apple.com/app/apple-store/id1503789749?pt=121219390&ct=NomadLyricsWebsite&mt=8"
                    title="iOS App" target="_blank" class="nl-btn-ghost">
                    App Store
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.nomadlyrics&utm_source=nomadlyrics.com&utm_source_platform=desktop"
                    title="Android App" target="_blank" class="nl-btn-ghost">
                    Google Play
                </a>
            </div>
        </section>
    </div>
    <Youtube :watching="watchVideo" @open-youtube="openYoutube" :id="song.youtube" v-if="watchVideo" />
    <lyrics-add :song="song" @open-lyrics="addLyrics" @submitted="reloadSong" v-if="addingLyrics"></lyrics-add>
</template>
<script setup>
import SongComments from '@/components/SongComments.vue';

defineProps({
    song: Object,
    initialComments: { type: Array, default: () => [] },
    commentsHaveMore: { type: Boolean, default: false },
});
</script>

<script>
import CardSong from "@/components/CardSong.vue";
import Featuring from "@/components/Featuring.vue";
import { Link, usePage, router } from "@inertiajs/vue3";
import { getBlurHashAverageColor } from 'fast-blurhash';

import { mapState, mapActions, mapGetters } from "vuex";

import { defineAsyncComponent } from 'vue'
import SmartPlay from "@/components/SmartPlay.vue";
import PageHeader from "@/components/ui/PageHeader.vue";
import TrackList from "@/components/ui/TrackList.vue";
import AdSlot from "@/components/ui/AdSlot.vue";

const page = usePage();
export default {
    data() {
        return {
            watchVideo: false,
            addingLyrics: false,
        };
    },
    beforeMount() {
        document.title = page.props.title
    },
    methods: {
        ...mapActions("favorites", ["toggleFavorite"]),
        ...mapActions("current", ["prepareCurrent"]),
        ...mapActions("notification", ["showNotification"]),
        openYoutube() {
            this.watchVideo = !this.watchVideo;
        },
        async copyLink() {
            navigator.clipboard.writeText("https://nomadlyrics.com/song/" + this.song.id);
            this.showNotification({
                message: 'link coppied!',
                type: 'info',
            })
            this.trackShare('Link', 'song', this.song.id)
        },
        addLyrics() {
            this.addingLyrics = !this.addingLyrics;
            this.$gtag.event("select_content", {
                content_type: "LYRICS::INIT",
                content_id: this.song.id
            });
        },
        reloadSong() {
            // Like the app re-fetching the song after a submission
            router.reload({ only: ['song'] });
        },
        trackShare(platform, type, id) {
            // Track the "SongPlay" event with the associated ID
            this.$gtag.event('share', {
                method: platform, //Facebook, Twitter, etc
                content_type: type, // image, song, event, episode
                song_id: id,
            });

        },
        backgroundFromBlurhash(blurhash) {
            if (!blurhash) {
                return '#1F201D'; // default background color
            }

            const rgbAverageColor = getBlurHashAverageColor(blurhash);
            return `rgba(${rgbAverageColor.join(',')}, 0.3)`; // adding some transparency

        },

    },
    components: {
        PageHeader,
        TrackList,
        AdSlot,
        "card-song": CardSong,
        featuring: Featuring,
        Link,
        Promo: defineAsyncComponent(() => import('@/components/Promo')),
        'Youtube': defineAsyncComponent(() => import('@/components/Youtube.vue')),
        'lyrics-add': defineAsyncComponent(() => import('@/components/LyricsAdd.vue')),
    },
    computed: {
        ...mapGetters("favorites", ["isLiked"]),
        lyricsText() {
            const verses = Array.isArray(this.song?.verse) ? this.song.verse : [];
            const submission = verses.find((verse) => verse.is_default && verse.lyrics?.non_synced_lyrics?.original)
                || verses.find((verse) => verse.lyrics?.non_synced_lyrics?.original);

            return submission?.lyrics?.non_synced_lyrics?.original || '';
        },
        imageCover() {
            // The original before meta.image: those smaller copies were squeezed to one shape
            return this.song.cover || this.song.meta?.image || this.song.artist.image;
        },
    },
};
</script>
